import {createAsyncThunk} from '@reduxjs/toolkit';
import type {MailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import type {SignupForm} from '@/form-maker/grapesjs-form-maker/src/form-maker.types';
import type {UserCustomFieldMeta} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {SmartMailingListTooltipMap} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import {
  getSidebarActivityIndicatorCookieValue,
  getSignupFormActivityIndicatorCookie,
  isPageRelatedToContacts,
} from '@Components/mailing-list-contacts/mailing-list-contacts-library';

export interface MailingListsResponse {
  mailingLists: MailingList[];
  smartMailingListsInfo?: SmartMailingListTooltipMap;
  hasMore: boolean;
}

export interface SignupFormsResponse {
  signupForms: SignupForm[];
  hasMore: boolean;
}

export interface SignupFormsActivityResponse {
  hasNewActivity: boolean;
}

export interface FetchRequestParams {
  offset: number;
  limit: number;
}

export interface SignupFormUpdateParams {
  signupFormIds: string[];
}

export interface SignupFormsUpdateResponse {
  signupFormsUpdateMap: Record<string, number>;
}

export const getSmartMailingListsForUser = createAsyncThunk<MailingListsResponse>('getSmartMailingListsForUser', async (): Promise<MailingListsResponse> => {
  return window.PMW.readLocal('emailmarketing/getSmartMailingListsForUser', {});
});

export const getUserCreatedMailingLists = createAsyncThunk<MailingListsResponse, FetchRequestParams>(
  'getUserCreatedMailingLists',
  async ({offset, limit}): Promise<MailingListsResponse> => {
    return window.PMW.readLocal('emailmarketing/getUserCreatedMailingLists', {
      offset: offset,
      limit: limit,
    });
  }
);

export const getUserSignupForms = createAsyncThunk<SignupFormsResponse, FetchRequestParams>('getUserSignupForms', async ({offset, limit}): Promise<SignupFormsResponse> => {
  return window.PMW.readLocal('emailmarketing/getUserSignupForms', {
    offset: offset,
    limit: limit,
  });
});

export const fetchSignupFormUpdateInformation = createAsyncThunk<SignupFormsUpdateResponse, SignupFormUpdateParams>(
  'fetchSignupFormUpdateInformation',
  async (params): Promise<SignupFormsUpdateResponse> => {
    return window.PMW.writeLocal('emailmarketing/fetchSignupFormUpdateInformation', {
      signupFormIds: params.signupFormIds,
    });
  }
);

export const fetchCustomFieldsMeta = createAsyncThunk<UserCustomFieldMeta[]>('fetchCustomFieldsMeta', async (): Promise<UserCustomFieldMeta[]> => {
  return window.PMW.writeLocal('emailmarketing/getUserCustomFieldsMeta');
});

export const fetchSignupFormsUpdateInformationForUser = createAsyncThunk<SignupFormsActivityResponse>(
  'fetchSignupFormsUpdateInformationForUser',
  async (): Promise<SignupFormsActivityResponse> => {
    const fetchSinceRelativeToSidebar = getSidebarActivityIndicatorCookieValue();
    const fetchSinceRelativeToSignupFormTab = getSignupFormActivityIndicatorCookie();

    let fetchSince = null;
    if (isPageRelatedToContacts()) {
      fetchSince = fetchSinceRelativeToSignupFormTab;
    } else {
      fetchSince = fetchSinceRelativeToSidebar;
    }

    return window.PMW.writeLocal('emailmarketing/fetchSignupFormsUpdateInformationForUser', {
      fetchSince: fetchSince,
    });
  }
);
